/**
 * useAdditionalViewData — F22 Ścieżka C (widoki dodatkowe, PHASE_22_PLAN.md §8).
 *
 * Pobiera dane widoku dodatkowego przez `POST /api/settings/additional-views/:viewId/data`
 * (`ExecuteAdditionalViewDataInput` → `AdditionalViewData`, `packages/shared/src/additional-views.ts`).
 * Mirror `useDictionaryOptions` (`apps/admin/src/hooks/useDictionary.ts`):
 *  - `staleTime: 0` — żadnego cache między wywołaniami, zawsze świeże dane po odświeżeniu.
 *  - `queryKey` zawiera zserializowane `input` (`bodyKey`) — inne wejście = inny klucz =
 *    zawsze świeże zapytanie sieciowe, nigdy trafienie w cache poprzedniego zestawu.
 *
 * Auto-refresh przy zmianie pola-parametru jest v2 (PHASE_22_PLAN.md §11) — TEN hook nie
 * decyduje KIEDY `input.recordValues` się zmienia, tylko wykonuje zapytanie dla wejścia,
 * jakie dostał. Caller (`AdditionalViewTab`) kontroluje moment odświeżenia (commit na mount
 * + przycisk „Odśwież", nie na każde naciśnięcie klawisza w polu formularza).
 *
 * GRANICA ZAUFANIA (T22 J1): `recordId` NIE jest częścią body (`ExecuteAdditionalViewDataInput`
 * już go nie niesie) — leci jako query param `?recordId=`, gdzie backend go AUTORYZUJE przez
 * `canAccessAdditionalParent` przed wykonaniem SQL. `companyId` w ogóle nie jest wysyłany —
 * backend liczy go WYŁĄCZNIE z nagłówka `X-Company-Id` (już dołączanego globalnie przez
 * `lib/api.ts` axios interceptor, patrz `getActiveCompanyId()`) zwalidowanego przeciw
 * `user_company_access`. Ten hook NIGDY nie wysyła `recordId`/`companyId` w ciele żądania.
 */

import type { AdditionalViewData, ExecuteAdditionalViewDataInput } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface UseAdditionalViewDataResult {
  data: AdditionalViewData | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * @param viewId   UUID widoku (`ext_additional_views.id`) — `null`/`undefined` gdy jeszcze
 *   nierozwiązany (np. katalog widoków tej kartoteki dopiero się ładuje) — query `enabled:false`.
 * @param recordId UUID rekordu-hosta (parent), przekazywany jako `?recordId=` — WYMAGANY przez
 *   backend do autoryzacji record-level (`canAccessAdditionalParent`). `null`/`undefined` →
 *   query `enabled:false` (widok nie ma jeszcze rekordu do związania, np. formularz tworzenia).
 * @param input    `ExecuteAdditionalViewDataInput` — `recordValues` powinno być WARTOŚCIĄ
 *   ZATWIERDZONĄ przez callera (nie live-typing), patrz nagłówek pliku.
 * @param enabled  Dodatkowa bramka callera (np. brak wypełnionych wymaganych parametrów →
 *   stan pusty zamiast zapytania sieciowego z niekompletnym wejściem).
 */
export function useAdditionalViewData(
  viewId: string | null | undefined,
  recordId: string | null | undefined,
  input: ExecuteAdditionalViewDataInput,
  enabled: boolean,
): UseAdditionalViewDataResult {
  const bodyKey = JSON.stringify(input);

  const query = useQuery({
    queryKey: ['additional-views', 'data', viewId ?? '', recordId ?? '', bodyKey],
    queryFn: async () => {
      const res = await api.post<AdditionalViewData>(
        `/settings/additional-views/${viewId}/data?recordId=${encodeURIComponent(recordId ?? '')}`,
        input,
      );
      return res.data;
    },
    staleTime: 0,
    enabled: enabled && !!viewId && !!recordId,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
